// src/portfolio/Portfolio.jsx — main IDE shell.
// Composes activity bar, file tree, tab bar, breadcrumb, editor, minimap,
// status bar, command palette. The single source of identity is data.js.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { THEMES, FILES, FILE_BY_ID, FILE_INDEX, DOCS, IDENTITY } from './data';
import { Icon, FileGlyph, MONO } from './icons';
import DocView from './DocView';
import CommandPalette from './CommandPalette';

// ── Persistent UI prefs (theme + minimap + linenum) ───────────────
const PREFS_KEY = 'portfolio.prefs.v1';
function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
}
function savePrefs(p) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* noop */ }
}

// ── Responsive helper ─────────────────────────────────────────────
function useIsMobile(breakpoint = 760) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// ── Activity bar ──────────────────────────────────────────────────
function ActivityBar({ theme, view, setView }) {
  const items = [
    { id: 'files',  Glyph: Icon.files,  hint: 'Explorer' },
    { id: 'search', Glyph: Icon.search, hint: 'Search' },
    { id: 'git',    Glyph: Icon.git,    hint: 'Source control' },
    { id: 'bug',    Glyph: Icon.bug,    hint: 'Run & debug' },
  ];
  const bottom = [
    { id: 'user', Glyph: Icon.user, hint: 'Profile' },
    { id: 'cog',  Glyph: Icon.cog,  hint: 'Settings' },
  ];
  const ItemRow = ({ id, Glyph, hint }) => {
    const active = view === id;
    return (
      <button
        key={id}
        type="button"
        title={hint}
        onClick={() => setView(id)}
        style={{
          width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: active ? theme.fgStrong : theme.dim,
          borderLeft: active ? `2px solid ${theme.accent}` : '2px solid transparent',
          transition: 'color .14s, border-color .14s',
          background: 'none', border: 'none', cursor: 'pointer',
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = theme.fg; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = theme.dim; }}
      >
        <Glyph width="22" height="22" />
      </button>
    );
  };
  return (
    <div style={{
      width: 48, background: theme.chrome, borderRight: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto',
    }}>
      <div style={{ paddingTop: 6 }}>{items.map(ItemRow)}</div>
      <div style={{ marginTop: 'auto', paddingBottom: 6 }}>{bottom.map(ItemRow)}</div>
    </div>
  );
}

// ── File tree ─────────────────────────────────────────────────────
function FileTree({ theme, activeId, onOpen, onRevealPalette }) {
  const [openMap, setOpenMap] = useState(() => {
    const m = {};
    const walk = (nodes) => nodes.forEach((n) => {
      if (n.kind === 'folder') { m[n.name] = !!n.open; walk(n.children); }
    });
    walk(FILES);
    return m;
  });
  const toggle = (name) => setOpenMap((m) => ({ ...m, [name]: !m[name] }));

  // sequential reveal index
  let order = 0;

  const Row = ({ node, depth }) => {
    if (node.kind === 'folder') {
      const open = openMap[node.name];
      const o = order++;
      return (
        <>
          <button
            type="button"
            onClick={() => toggle(node.name)}
            className="row-in"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 4,
              padding: `4px 8px 4px ${8 + depth * 12}px`,
              color: theme.fg, fontFamily: MONO, fontSize: 12,
              borderRadius: 4, transition: 'background .1s',
              animationDelay: `${40 + o * 14}ms`,
              background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = theme.surface2)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <span style={{
              color: theme.dim, fontSize: 9, width: 10,
              transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block',
            }}>▶</span>
            <span style={{ color: theme.sx.head, fontSize: 12 }}>▸</span>
            <span style={{ color: theme.fg }}>{node.name}/</span>
          </button>
          {open && node.children.map((c, i) => <Row key={(c.id || c.name) + i} node={c} depth={depth + 1} />)}
        </>
      );
    }
    const active = activeId === node.id;
    const o = order++;
    return (
      <button
        type="button"
        onClick={() => onOpen(node.id)}
        className="row-in"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6,
          padding: `4px 8px 4px ${4 + depth * 12}px`,
          color: active ? theme.fgStrong : theme.fg, fontFamily: MONO, fontSize: 12,
          background: active ? theme.surface2 : 'transparent',
          borderRadius: 4, transition: 'background .1s, color .1s',
          textAlign: 'left', border: 0, cursor: 'pointer',
          animationDelay: `${40 + o * 14}ms`,
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = theme.surface; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <FileGlyph kind={node.kind} theme={theme} size={12} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
        {node.badge && <span style={{ fontSize: 9, color: theme.dim, letterSpacing: '.04em' }}>{node.badge}</span>}
      </button>
    );
  };

  return (
    <div style={{
      width: 248, background: theme.surface, borderRight: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', flex: '0 0 auto', overflow: 'hidden',
    }}>
      <div style={{ padding: '10px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', color: theme.mute, fontWeight: 600 }}>Explorer</span>
        <span style={{ fontSize: 9, color: theme.dim, fontFamily: MONO }}>portfolio</span>
      </div>
      <div style={{ padding: '0 12px 6px' }}>
        <button
          type="button"
          onClick={onRevealPalette}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 8px', borderRadius: 4, background: theme.surface2,
            border: `1px solid ${theme.border}`, fontSize: 11, color: theme.mute, fontFamily: MONO,
            textAlign: 'left', cursor: 'pointer',
          }}
        >
          <span style={{ color: theme.dim }}>⌘ K</span>
          <span>open anything…</span>
        </button>
      </div>
      <div style={{ padding: '4px 6px 14px', overflow: 'auto', flex: 1 }}>
        <div style={{
          padding: '2px 8px', fontSize: 11, color: theme.fg, fontFamily: MONO, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <span style={{ color: theme.dim, fontSize: 9, transform: 'rotate(90deg)', display: 'inline-block' }}>▶</span>
          <span>{IDENTITY.github.toLowerCase()}-portfolio/</span>
        </div>
        {FILES.map((n, i) => <Row key={n.name + i} node={n} depth={1} />)}
      </div>
      <div style={{
        padding: '10px 14px', borderTop: `1px solid ${theme.border}`,
        fontSize: 10, color: theme.dim, fontFamily: MONO, display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Outline</span><span>—</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Timeline</span><span>—</span></div>
      </div>
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────
function TabBar({ theme, tabs, activeId, onSelect, onClose, isMobile }) {
  return (
    <div style={{
      display: 'flex', background: theme.chrome, borderBottom: `1px solid ${theme.border}`,
      height: isMobile ? 40 : 36, overflowX: 'auto', overflowY: 'hidden', flex: '0 0 auto',
      scrollbarWidth: 'none',
    }}>
      {tabs.map((id) => {
        const f = FILE_BY_ID[id];
        const active = activeId === id;
        return (
          <div
            key={id}
            className="tab-in"
            onClick={() => onSelect(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '0 12px', cursor: 'pointer', fontFamily: MONO, fontSize: 12,
              background: active ? theme.bg : 'transparent',
              color: active ? theme.fgStrong : theme.mute,
              borderRight: `1px solid ${theme.border}`,
              borderTop: active ? `1.5px solid ${theme.accent}` : '1.5px solid transparent',
              transition: 'background .12s, color .12s', position: 'relative', minWidth: 0,
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = theme.fg; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = theme.mute; }}
          >
            <FileGlyph kind={f?.kind ?? 'md'} theme={theme} size={12} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{f?.name}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClose(id); }}
              style={{
                width: 18, height: 18, borderRadius: 4, marginLeft: 4,
                color: theme.dim, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, lineHeight: 1, opacity: active ? 1 : 0.7,
                transition: 'background .12s, color .12s', background: 'none', border: 0, cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = theme.surface2; e.currentTarget.style.color = theme.fgStrong; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent';  e.currentTarget.style.color = theme.dim; }}
            >×</button>
          </div>
        );
      })}
      <div style={{ flex: 1, minWidth: 12 }} />
      {!isMobile && (
        <div style={{ padding: '0 14px', display: 'flex', alignItems: 'center', gap: 14, color: theme.dim, fontFamily: MONO, fontSize: 11, flex: '0 0 auto' }}>
          <span>⌘ K  palette</span>
        </div>
      )}
    </div>
  );
}

function Breadcrumb({ theme, file }) {
  if (!file) return null;
  const parts = file.path.split('/');
  return (
    <div style={{
      padding: '5px 18px', borderBottom: `1px solid ${theme.border}`,
      fontFamily: MONO, fontSize: 11, color: theme.dim,
      display: 'flex', gap: 6, alignItems: 'center', background: theme.bg,
    }}>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          <span style={{ color: i === parts.length - 1 ? theme.fg : theme.dim }}>{p}</span>
          {i < parts.length - 1 && <span style={{ color: theme.dim }}>›</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Minimap ───────────────────────────────────────────────────────
function Minimap({ theme, scrollPct, doc }) {
  const slats = useMemo(() => {
    if (!doc) return [];
    const arr = [];
    for (const b of doc.blocks) {
      if (b.type === 'h1' || b.type === 'h2' || b.type === 'h3') {
        arr.push({ w: 0.7, h: 5, c: theme.sx.head });
        arr.push({ w: 0, h: 4, c: 'transparent' });
      } else if (b.type === 'subtitle' || b.type === 'meta') {
        arr.push({ w: 0.55, h: 2.5, c: theme.dim });
        arr.push({ w: 0, h: 2, c: 'transparent' });
      } else if (b.type === 'p') {
        for (let i = 0; i < 4; i++) arr.push({ w: 0.6 + Math.random() * 0.35, h: 2.5, c: theme.fg });
        arr.push({ w: 0, h: 2, c: 'transparent' });
      } else if (b.type === 'ul' || b.type === 'fileref') {
        for (const _it of (b.items || [])) arr.push({ w: 0.55, h: 2.5, c: theme.sx.link });
        arr.push({ w: 0, h: 2, c: 'transparent' });
      } else if (b.type === 'chips' || b.type === 'kv' || b.type === 'table') {
        for (let i = 0; i < 3; i++) arr.push({ w: 0.85, h: 2.5, c: theme.sx.num });
        arr.push({ w: 0, h: 2, c: 'transparent' });
      } else if (b.type === 'callout') {
        arr.push({ w: 0.85, h: 8, c: theme.sx.link, opacity: 0.4 });
        arr.push({ w: 0, h: 3, c: 'transparent' });
      } else if (b.type === 'code') {
        const lines = (b.text || '').split('\n').length;
        for (let i = 0; i < Math.min(lines, 14); i++) arr.push({ w: 0.45 + Math.random() * 0.4, h: 2.5, c: theme.sx.str });
        arr.push({ w: 0, h: 2, c: 'transparent' });
      } else if (b.type === 'youtube') {
        arr.push({ w: 0.9, h: 30, c: theme.sx.tag, opacity: 0.5 });
        arr.push({ w: 0, h: 3, c: 'transparent' });
      } else if (b.type === 'hero') {
        arr.push({ w: 0.95, h: 40, c: theme.accent, opacity: 0.6 });
        arr.push({ w: 0, h: 4, c: 'transparent' });
      } else if (b.type === 'cards') {
        for (let i = 0; i < ((b.items || []).length || 2); i++) arr.push({ w: 0.7, h: 14, c: theme.sx.link, opacity: 0.4 });
        arr.push({ w: 0, h: 3, c: 'transparent' });
      } else if (b.type === 'pdf') {
        arr.push({ w: 0.9, h: 60, c: theme.surface });
      }
    }
    return arr;
  }, [doc, theme]);

  const total = slats.reduce((a, s) => a + s.h, 0) || 1;
  const viewH = Math.max(40, total * 0.35);
  const viewTop = (total - viewH) * scrollPct;

  return (
    <div style={{
      position: 'relative', borderLeft: `1px solid ${theme.border}`,
      background: theme.bg, overflow: 'hidden', padding: '36px 8px 12px',
    }}>
      <div style={{ position: 'relative' }}>
        {slats.map((s, i) => (
          <div key={i} style={{
            height: s.h, marginBottom: 1, width: `${s.w * 100}%`, background: s.c,
            opacity: s.opacity ?? (s.c === 'transparent' ? 0 : 0.55), borderRadius: 1,
          }} />
        ))}
        <div style={{
          position: 'absolute', left: -4, right: -4, top: viewTop, height: viewH,
          background: 'rgba(255,255,255,0.04)',
          borderTop: `1px solid ${theme.borderHi}`, borderBottom: `1px solid ${theme.borderHi}`,
          pointerEvents: 'none',
        }} />
      </div>
    </div>
  );
}

// ── Editor pane (line numbers + content + minimap) ────────────────
function EditorPane({ theme, doc, identity, onOpen, showLineNumbers, showMinimap, isMobile }) {
  const scrollRef = useRef(null);
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      setScrollPct(max > 0 ? el.scrollTop / max : 0);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.scrollTop = 0; setScrollPct(0);
    return () => el.removeEventListener('scroll', onScroll);
  }, [doc?.title]);

  const gridCols = [showLineNumbers && '52px', '1fr', showMinimap && '76px'].filter(Boolean).join(' ');

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: gridCols,
      flex: 1, minHeight: 0, background: theme.bg, position: 'relative',
    }}>
      {showLineNumbers && (
        <div style={{
          background: theme.bg, color: theme.dim, fontFamily: MONO, fontSize: 11,
          paddingTop: 36, textAlign: 'right', paddingRight: 10,
          borderRight: `1px solid ${theme.border}`, lineHeight: 1.62, overflow: 'hidden', userSelect: 'none',
        }}>
          {Array.from({ length: 220 }).map((_, i) => <div key={i} style={{ height: 23 }}>{i + 1}</div>)}
        </div>
      )}
      <div ref={scrollRef} style={{ overflow: 'auto', position: 'relative' }}>
        <DocView doc={doc} theme={theme} identity={identity} onOpen={onOpen} isMobile={isMobile} />
      </div>
      {showMinimap && <Minimap theme={theme} scrollPct={scrollPct} doc={doc} />}
    </div>
  );
}

function StatusBar({ theme, file, identity, isMobile }) {
  return (
    <div style={{
      height: 24, background: theme.accent, color: theme.bg,
      display: 'flex', alignItems: 'center', gap: 14, padding: '0 12px',
      fontFamily: MONO, fontSize: 11, flex: '0 0 auto', letterSpacing: '.02em',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: theme.bg }} className="tick" />
        <span>main</span>
      </span>
      {!isMobile && <span>↑ 0 ↓ 0</span>}
      <span style={{ marginLeft: 'auto', display: 'flex', gap: isMobile ? 10 : 14 }}>
        <span>{
          file?.kind === 'md'   ? 'Markdown' :
          file?.kind === 'json' ? 'JSON'     :
          file?.kind === 'pdf'  ? 'PDF'      :
          file?.kind === 'tsv'  ? 'TSV'      : 'Plain'
        }</span>
        {!isMobile && <span>UTF-8</span>}
        {!isMobile && <span>LF</span>}
        {!isMobile && <span>Ln 1, Col 1</span>}
        <span style={{ opacity: 0.8 }}>{identity.email}</span>
      </span>
    </div>
  );
}

function Toast({ theme, msg }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)',
      background: theme.surface2, color: theme.fg, border: `1px solid ${theme.borderHi}`,
      borderLeft: `3px solid ${theme.accent}`,
      padding: '10px 16px', borderRadius: 6, fontFamily: MONO, fontSize: 12,
      boxShadow: '0 8px 28px rgba(0,0,0,0.5)', zIndex: 200,
      animation: 'palette-in .18s cubic-bezier(0.2, 0.7, 0.2, 1)',
    }}>{msg}</div>
  );
}

// ── App ───────────────────────────────────────────────────────────
export default function Portfolio() {
  const [prefs, setPrefs] = useState(() => ({
    theme: 'dark', showMinimap: true, showLineNumbers: true, ...loadPrefs(),
  }));
  useEffect(() => { savePrefs(prefs); }, [prefs]);
  const setPref = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const theme = useMemo(() => THEMES[prefs.theme] ?? THEMES.dark, [prefs.theme]);
  const identity = IDENTITY;
  const isMobile = useIsMobile();

  const [tabs, setTabs] = useState(['about']);
  const [active, setActive] = useState('about');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [activityView, setActivityView] = useState('files');
  const [toast, setToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openFile = useCallback((id) => {
    if (!FILE_BY_ID[id]) return;
    setTabs((t) => (t.includes(id) ? t : [...t, id]));
    setActive(id);
    setDrawerOpen(false);
  }, []);

  const closeTab = useCallback((id) => {
    setTabs((t) => {
      const next = t.filter((x) => x !== id);
      if (id === active) {
        const i = t.indexOf(id);
        const nb = next[i] ?? next[i - 1];
        setActive(nb ?? null);
      }
      return next.length ? next : ['about'];
    });
  }, [active]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 1900); };

  const runCommand = useCallback((id) => {
    switch (id) {
      case 'cmd.toggle-theme':
        setPref('theme', prefs.theme === 'dark' ? 'light' : 'dark'); break;
      case 'cmd.toggle-minimap':
        setPref('showMinimap', !prefs.showMinimap); break;
      case 'cmd.toggle-linenum':
        setPref('showLineNumbers', !prefs.showLineNumbers); break;
      case 'cmd.copy-email':
        navigator.clipboard?.writeText(identity.email); showToast(`Copied — ${identity.email}`); break;
      case 'cmd.open-github':
        window.open(`https://github.com/${identity.github}`, '_blank'); break;
      case 'cmd.open-linkedin':
        window.open(`https://linkedin.com/in/${identity.linkedin}`, '_blank'); break;
      case 'cmd.download-resume':
        openFile('resume'); showToast('Showing resume.pdf — print to save'); break;
      case 'cmd.close-tab':
        if (active) closeTab(active); break;
      case 'cmd.close-all':
        setTabs(['about']); setActive('about'); break;
      default: break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs, identity, active, closeTab, openFile]);

  const onPick = (p) => {
    setPaletteOpen(false);
    if (p.kind === 'file') openFile(p.fileId);
    else runCommand(p.id);
  };

  // global keyboard
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'k' || e.key === 'K' || e.key === 'p' || e.key === 'P')) {
        e.preventDefault(); setPaletteOpen((o) => !o);
      } else if (mod && (e.key === 'w' || e.key === 'W')) {
        e.preventDefault(); if (active) closeTab(active);
      } else if (mod && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault(); runCommand('cmd.copy-email');
      } else if (mod && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault(); runCommand('cmd.open-github');
      } else if (mod && (e.key === 'l' || e.key === 'L')) {
        e.preventDefault(); runCommand('cmd.open-linkedin');
      } else if (mod && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault(); runCommand('cmd.download-resume');
      } else if (mod && (e.key === 't' || e.key === 'T')) {
        e.preventDefault(); runCommand('cmd.toggle-theme');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, closeTab, runCommand]);

  const file = active ? FILE_BY_ID[active] : null;
  const doc = file ? DOCS[file.id] : null;

  return (
    <div className="app-in" style={{
      width: '100%', height: '100vh', background: theme.bg, color: theme.fg,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Title bar */}
      <div style={{
        height: 32, background: theme.chrome, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', padding: '0 12px', gap: 10, flex: '0 0 auto',
      }}>
        {isMobile && (
          <button
            type="button"
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="Toggle file menu"
            style={{
              width: 30, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.fg, marginRight: 2,
            }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        )}
        <span style={{ display: 'flex', gap: 6 }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: theme.trafficRed }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: theme.trafficYellow }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: theme.trafficGreen }} />
        </span>
        <span style={{ flex: 1, textAlign: 'center', fontSize: 11.5, color: theme.mute, fontFamily: MONO, letterSpacing: '.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {isMobile ? (file?.name ?? '') : `${identity.github.toLowerCase()}-portfolio — ${file?.name ?? ''}`}
        </span>
        {isMobile ? (
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
            style={{ width: 30, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.fg }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="6" /><path d="M20 20l-4.3-4.3" />
            </svg>
          </button>
        ) : (
          <span style={{ fontSize: 10, color: theme.dim, fontFamily: MONO }}>v2026.5</span>
        )}
      </div>

      {/* Main row */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        {!isMobile && <ActivityBar theme={theme} view={activityView} setView={setActivityView} />}

        {!isMobile && (
          <FileTree theme={theme} activeId={active} onOpen={openFile} onRevealPalette={() => setPaletteOpen(true)} />
        )}
        {isMobile && drawerOpen && (
          <>
            <div
              onClick={() => setDrawerOpen(false)}
              style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
                zIndex: 50, backdropFilter: 'blur(2px)', animation: 'fade-in .18s both',
              }}
            />
            <div className="drawer-in" style={{
              position: 'absolute', top: 0, bottom: 0, left: 0,
              zIndex: 60, boxShadow: '4px 0 28px rgba(0,0,0,0.5)',
              display: 'flex',
            }}>
              <FileTree
                theme={theme}
                activeId={active}
                onOpen={openFile}
                onRevealPalette={() => { setPaletteOpen(true); setDrawerOpen(false); }}
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <TabBar theme={theme} tabs={tabs} activeId={active} onSelect={setActive} onClose={closeTab} isMobile={isMobile} />
          {!isMobile && <Breadcrumb theme={theme} file={file} />}
          <EditorPane
            theme={theme}
            doc={doc}
            identity={identity}
            onOpen={openFile}
            showLineNumbers={!isMobile && prefs.showLineNumbers}
            showMinimap={!isMobile && prefs.showMinimap}
            isMobile={isMobile}
          />
        </div>
      </div>

      <StatusBar theme={theme} file={file} identity={identity} isMobile={isMobile} />

      <CommandPalette theme={theme} open={paletteOpen} onClose={() => setPaletteOpen(false)} onPick={onPick} />
      <Toast theme={theme} msg={toast} />
    </div>
  );
}
